import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, input, output, inject, effect, untracked } from '@angular/core';

import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// PrimeNG imports
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DatePickerModule } from 'primeng/datepicker';
import { TableModule } from 'primeng/table';

// NgxFileDrop for file upload
import { NgxFileDropModule, NgxFileDropEntry } from 'ngx-file-drop';

// Models and services
import { 
  ProductSkuResponse, 
  CreateProductSkuRequest, 
  UpdateProductSkuRequest,
  ProductSkuAttribute,
} from '../../models/product-sku.model';
import { ProductResponse, SupplierResponse } from '../../models';
import { ProductSkuService, SupplierService } from '../../services';
import { RETAIL_LOOKUP_PAGE_SIZE } from '../../constants/retail.constants';
import { buildRetailImageWritePayload } from '../../utils/retail-image.util';
import { CommonService, ToastMessageService } from '@fitness-force/api';
import { TextareaModule } from 'primeng/textarea';

@Component({
  selector: 'lib-sku-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    InputNumberModule,
    SelectModule,
    ToggleButtonModule,
    ToggleSwitchModule,
    DatePickerModule,
    TableModule,
    NgxFileDropModule
],
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: 'min(95vw, 1000px)', height: 'min(95vh, 800px)' }"
      [contentStyle]="{ padding: '0', height: '100%' }"
      [maximizable]="true"
      (onHide)="onCancel.emit()">
    
      <ng-template pTemplate="header">
        <div class="flex align-items-center gap-3">
          <i class="pi pi-box text-xl text-primary"></i>
          <h3 class="m-0">{{ isEditMode ? ('RETAIL_BILLING.SKU.EDIT' | translate) : ('RETAIL_BILLING.SKU.CREATE' | translate) }}</h3>
          <div class="bg-primary-50 border-primary-200 border-1 border-round px-2 py-1 text-xs text-primary font-medium ml-auto">
            {{ companyLevel() ? ('RETAIL_BILLING.PRODUCTS.COMPANY_LEVEL' | translate) : ('RETAIL_BILLING.PRODUCTS.CURRENT_TENANT' | translate) }}
          </div>
        </div>
      </ng-template>
    
      <div class="h-full flex flex-column">
        <div class="flex-1 overflow-y-auto p-4" style="height: calc(100% - 80px);">
          <form [formGroup]="skuForm">
            <!-- SKU Configuration Section -->
            <div class="surface-card border-round p-3 mb-3 shadow-1">
              <div class="flex align-items-center gap-2 mb-3">
                <i class="pi pi-cog text-primary"></i>
                <h5 class="m-0 font-semibold">SKU Configuration</h5>
              </div>
              <!-- Structured Column Layout -->
              <div class="grid">
                <!-- Column 1 -->
                <div class="col-12 md:col-6">
                  <!-- SKU Name (Primary Language) -->
                  <div class="field mb-3">
                    <label for="sku_name_primary" class="block font-medium mb-2">
                      {{ 'RETAIL_BILLING.SKU.SKU_NAME' | translate }}
                      @if (isSecondaryLanguage()) {
                        <span>({{ primaryLanguage() }})</span>
                      }
                      <sup class="text-red-500">*</sup>
                    </label>
                    <input
                      pInputText
                      id="sku_name_primary"
                      formControlName="variant_name_primary_language"
                      class="w-full"
                      placeholder="e.g., Red T-Shirt - Large"
                      data-testid="sku-name-primary-input" />
                      @if (skuForm.get('variant_name_primary_language')?.invalid && skuForm.get('variant_name_primary_language')?.touched) {
                        <small class="p-error">
                          {{ 'RETAIL_BILLING.SKU.SKU_NAME' | translate }} is required
                        </small>
                      }
                    </div>
    
                    <!-- SKU Code -->
                    <div class="field mb-3">
                      <label for="sku_code" class="block font-medium mb-2">
                        {{ 'RETAIL_BILLING.SKU.SKU_CODE' | translate }}
                      </label>
                      <input
                        pInputText
                        id="sku_code"
                        formControlName="sku_code"
                        class="w-full"
                        placeholder="Auto-generated if empty"
                        data-testid="sku-code-input" />
                      </div>
    
                      <!-- Unit Price -->
                      <div class="field mb-3">
                        <label for="unit_price" class="block font-medium mb-2">
                          {{ 'RETAIL_BILLING.SKU.UNIT_PRICE' | translate }}
                          <sup class="text-red-500">*</sup>
                        </label>
                        <p-inputNumber id="unit_price"
                          formControlName="unit_price_minor"
                          class="w-full"
                          [min]="0"
                          mode="decimal"
                          [useGrouping]="false"
                          placeholder="0.00"
                          data-testid="unit-price-input" />
                        @if (skuForm.get('unit_price_minor')?.invalid && skuForm.get('unit_price_minor')?.touched) {
                          <small class="p-error">
                            {{ 'RETAIL_BILLING.SKU.UNIT_PRICE' | translate }} is required
                          </small>
                        }
                      </div>

                      <!-- Online Price -->
                      <div class="field mb-3">
                        <label for="online_price" class="block font-medium mb-2">
                          {{ 'RETAIL_BILLING.SKU.ONLINE_PRICE' | translate }}
                        </label>
                        <p-inputNumber id="online_price"
                          formControlName="online_price_minor"
                          class="w-full"
                          [min]="0"
                          mode="decimal"
                          [useGrouping]="false"
                          placeholder="0.00"
                          data-testid="online-price-input" />
                      </div>
    
                      <!-- Barcode -->
                      <div class="field mb-3">
                        <label for="barcode" class="block font-medium mb-2">
                          {{ 'RETAIL_BILLING.SKU.BARCODE' | translate }}
                        </label>
                        <input
                          pInputText
                          id="barcode"
                          formControlName="barcode"
                          class="w-full"
                          placeholder="Scan or enter"
                          data-testid="barcode-input" />
                        </div>
    
                        <!-- Reorder Level -->
                        <div class="field mb-3">
                          <label for="reorder_level" class="block font-medium mb-2">
                            {{ 'RETAIL_BILLING.SKU.REORDER_LEVEL' | translate }}
                          </label>
                          <p-inputNumber id="reorder_level"
                            formControlName="reorder_level"
                            class="w-full"
                            [min]="0"
                            type="decimal"
                            [useGrouping]="false"
                            [showButtons]="true"
                            placeholder="0"
                            data-testid="reorder-level-input" />
                        </div>
    
                        <!-- Track Inventory -->
                        <div class="field mb-3">
                          <div class="flex align-items-center gap-3">
                            <label for="track_inventory" class="font-medium">
                              {{ 'RETAIL_BILLING.SKU.TRACK_INVENTORY' | translate }}
                            </label>
                            <p-toggleSwitch id="track_inventory"
                              formControlName="track_inventory"
                              data-testid="track-inventory-toggle" />
                          </div>
                        </div>
                      </div>
    
                      <!-- Column 2 -->
                      <div class="col-12 md:col-6">
                        <!-- SKU Name (Secondary Language) -->
                        @if (isSecondaryLanguage()) {
                          <div class="field mb-3">
                            <label for="sku_name_secondary" class="block font-medium mb-2">
                              {{ 'RETAIL_BILLING.SKU.SKU_NAME' | translate }}
                              <span>({{ secondaryLanguage() }})</span>
                              <sup class="text-red-500">*</sup>
                            </label>
                            <input
                              pInputText
                              id="sku_name_secondary"
                              formControlName="variant_name_secondary_language"
                              class="w-full"
                              dir="rtl"
                              data-testid="sku-name-secondary-input" />
                              @if (skuForm.get('variant_name_secondary_language')?.invalid && skuForm.get('variant_name_secondary_language')?.touched) {
                                <small class="p-error">
                                  {{ 'RETAIL_BILLING.SKU.SKU_NAME' | translate }} is required
                                </small>
                              }
                            </div>
                          }
    
                          <!-- Cost Price -->
                          <div class="field mb-3">
                            <label for="cost_price" class="block font-medium mb-2">
                              {{ 'RETAIL_BILLING.SKU.COST_PRICE' | translate }}
                              <sup class="text-red-500">*</sup>
                            </label>
                            <p-inputNumber id="cost_price"
                              formControlName="cost_price_minor"
                              class="w-full"
                              [min]="0"
                              mode="decimal"
                              [useGrouping]="false"
                              placeholder="0.00"
                              data-testid="cost-price-input" />
                            @if (skuForm.get('cost_price_minor')?.invalid && skuForm.get('cost_price_minor')?.touched) {
                              <small class="p-error">
                                {{ 'RETAIL_BILLING.SKU.COST_PRICE' | translate }} is required
                              </small>
                            }
                          </div>

                          <!-- Shipping Weight -->
                          <div class="field mb-3">
                            <label for="shipping_weight" class="block font-medium mb-2">
                              {{ 'RETAIL_BILLING.SKU.SHIPPING_WEIGHT' | translate }}
                            </label>
                            <p-inputNumber id="shipping_weight"
                              formControlName="shipping_weight"
                              class="w-full"
                              [min]="0"
                              mode="decimal"
                              [useGrouping]="false"
                              [minFractionDigits]="0"
                              [maxFractionDigits]="4"
                              placeholder="0"
                              data-testid="shipping-weight-input" />
                          </div>

                          <!-- Supplier -->
                          <div class="field mb-3">
                            <label for="supplier_id" class="block font-medium mb-2">
                              {{ 'RETAIL_BILLING.SKU.SUPPLIER' | translate }}
                            </label>
                            <p-select id="supplier_id"
                              formControlName="supplier_id"
                              [options]="supplierOptions"
                              optionLabel="name"
                              optionValue="value"
                              [placeholder]="'RETAIL_BILLING.SKU.SELECT_SUPPLIER' | translate"
                              [showClear]="true"
                              class="w-full"
                              aria-label="supplier-dropdown" />
                          </div>

                          <!-- Supplier SKU ID -->
                          <div class="field mb-3">
                            <label for="supplier_sku_id" class="block font-medium mb-2">
                              {{ 'RETAIL_BILLING.SKU.SUPPLIER_SKU_ID' | translate }}
                            </label>
                            <input
                              pInputText
                              id="supplier_sku_id"
                              formControlName="supplier_sku_id"
                              class="w-full"
                              data-testid="supplier-sku-id-input" />
                          </div>
    
                          <!-- Unit of Measure -->
                          <div class="field mb-3">
                            <label for="inventory_uom" class="block font-medium mb-2">
                              {{ 'RETAIL_BILLING.SKU.INVENTORY_UOM' | translate }}
                            </label>
                            <input
                              pInputText
                              id="inventory_uom"
                              formControlName="inventory_uom"
                              class="w-full"
                              placeholder="e.g., pcs, kg, liters"
                              data-testid="inventory-uom-input" />
                            </div>
    
                            <!-- Maximum Inventory -->
                            <div class="field mb-3">
                              <label for="max_inventory" class="block font-medium mb-2">
                                {{ 'RETAIL_BILLING.SKU.MAX_INVENTORY' | translate }}
                              </label>
                              <p-inputNumber id="max_inventory"
                                formControlName="max_inventory"
                                class="w-full"
                                [min]="0"
                                [useGrouping]="false"
                                [showButtons]="true"
                                placeholder="0"
                                data-testid="max-inventory-input" />
                            </div>
    
    
                            <!-- Is Default -->
                            <div class="field mb-3">
                              <div class="flex align-items-center gap-3">
                                <label for="is_default" class="font-medium">
                                  {{ 'RETAIL_BILLING.SKU.IS_DEFAULT' | translate }}
                                </label>
                                <p-toggleSwitch id="is_default"
                                  formControlName="is_default"
                                  data-testid="is-default-toggle" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Description Section -->
                      <div class="surface-card border-round p-3 mb-3 shadow-1">
                        <div class="flex align-items-center gap-2 mb-3">
                          <i class="pi pi-align-left text-primary"></i>
                          <h5 class="m-0 font-semibold">{{ 'RETAIL_BILLING.SKU.DESCRIPTION' | translate }}</h5>
                        </div>
                        <div class="grid">
                          <div class="col-12 md:col-6">
                            <label for="desc_primary" class="block font-medium mb-2">
                              {{ 'RETAIL_BILLING.SKU.DESCRIPTION' | translate }}
                              @if (isSecondaryLanguage()) {
                                <span>({{ primaryLanguage() }})</span>
                              }
                            </label>
                            <textarea
                              pInputTextarea
                              id="desc_primary"
                              formControlName="description_primary_language"
                              rows="3"
                              class="w-full"
                              data-testid="sku-description-primary-input">
                            </textarea>
                          </div>
                          @if (isSecondaryLanguage()) {
                            <div class="col-12 md:col-6">
                              <label for="desc_secondary" class="block font-medium mb-2">
                                {{ 'RETAIL_BILLING.SKU.DESCRIPTION' | translate }}
                                <span>({{ secondaryLanguage() }})</span>
                              </label>
                              <textarea
                                pInputTextarea
                                id="desc_secondary"
                                formControlName="description_secondary_language"
                                rows="3"
                                class="w-full"
                                dir="rtl"
                                data-testid="sku-description-secondary-input">
                              </textarea>
                            </div>
                          }
                        </div>
                      </div>
    
                      <!-- Image Upload Section -->
                      <div class="surface-card border-round p-3 mb-3 shadow-1">
                        <div class="flex align-items-center gap-2 mb-3">
                          <i class="pi pi-image text-primary"></i>
                          <h5 class="m-0 font-semibold">{{ 'RETAIL_BILLING.PRODUCTS.IMAGE' | translate }}</h5>
                        </div>
    
                        <div class="grid">
                          <div class="col-12 md:col-4">
                            <div class="border-2 border-dashed border-300 border-round p-3 text-center h-full"
                              style="min-height: 120px; position: relative;">
    
                              <!-- Show uploaded image if exists -->
                              @if (currentImageUrl) {
                                <div class="h-full flex flex-column align-items-center justify-content-center">
                                  <img [src]="currentImageUrl" alt="SKU image" class="max-w-full max-h-full border-round mb-2" style="max-height: 80px;" />
                                  <p-button icon="pi pi-times"
                                    [text]="true"
                                    [rounded]="true"
                                    size="small"
                                    severity="danger"
                                    (onClick)="removeImage()"
                                    aria-label="remove-image-button" />
                                </div>
                              }
    
                              <!-- File drop zone -->
                              @if (!currentImageUrl) {
                                <div class="h-full flex flex-column align-items-center justify-content-center">
                                  <ngx-file-drop
                                    class="opacity-0 absolute w-full h-full top-0 left-0 z-4 cursor-pointer"
                                    [accept]="'.png,.jpg,.jpeg,.gif'"
                                    (onFileDrop)="handleFileSelect($event)"
                                    aria-label="file-drop-zone">
                                    <ng-template ngx-file-drop-content-tmp let-openFileSelector="openFileSelector">
                                      <div (click)="openFileSelector()" class="w-full h-full absolute left-0 top-0" aria-label="file-drop-zone-click"></div>
                                    </ng-template>
                                  </ngx-file-drop>
                                  <i class="pi pi-cloud-upload text-3xl text-400 mb-2"></i>
                                  <p class="text-xs text-600 text-center line-height-3 m-0">
                                    {{ 'RETAIL_BILLING.COMMON.DRAG_HERE' | translate }}<br/>
                                    <span class="text-primary">{{ 'RETAIL_BILLING.COMMON.BROWSE_FILE' | translate }}</span>
                                  </p>
                                </div>
                              }
                            </div>
                          </div>
    
                          <div class="col-12 md:col-8">
                            <div class="text-sm text-600 line-height-3">
                              <p class="mt-0 mb-2"><strong>Image Guidelines:</strong></p>
                              <ul class="pl-3 mb-0 text-xs">
                                <li>Recommended size: 400x400px or larger</li>
                                <li>Maximum file size: 5MB</li>
                                <li>Supported formats: JPEG, PNG, GIF</li>
                                <li>Use high-quality images for better presentation</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
    
                      <!-- Product Attributes Section -->
                      @if (productAttributeSchema && productAttributeSchema.length > 0) {
                        <div class="surface-card border-round p-3 mb-3 shadow-1">
                          <div class="flex align-items-center gap-2 mb-3">
                            <i class="pi pi-sliders-h text-primary"></i>
                            <h5 class="m-0 font-semibold">{{ 'RETAIL_BILLING.SKU.PRODUCT_ATTRIBUTES' | translate }}</h5>
                            <span class="bg-primary text-white border-round px-2 py-1 text-xs ml-auto">
                              {{ productAttributeSchema.length }} {{ productAttributeSchema.length === 1 ? 'Attribute' : 'Attributes' }}
                            </span>
                          </div>
                          <div class="grid gap-2">
                            @for (schema of productAttributeSchema; track trackBySchema(i, schema); let i = $index) {
                              <div
                                class="col-12 md:col-6 lg:col-4">
                                <div class="field">
                                  <!-- Enum Type (Dropdown) -->
                                  @if (schema.type.toLowerCase() === 'enum') {
                                    <div data-testid="attribute-enum-container">
                                      <label [for]="'attr_' + i" class="block text-sm font-medium text-700 mb-1">
                                        <i class="pi pi-list text-xs mr-1 text-primary"></i>
                                        {{ schema.label || schema.name }}
                                        @if (schema.required) {
                                          <sup class="text-red-500">*</sup>
                                        }
                                      </label>
                                      <p-select
                                        [id]="'attr_' + i"
                                        [formControlName]="'attr_' + schema.name"
                                        [options]="schema.allowed_values"
                                        [placeholder]="'Select ' + (schema.label || schema.name)"
                                        class="w-full"
                                        [showClear]="true"
                                        data-testid="attribute-select">
                                        <ng-template pTemplate="selectedItem">
                                          <div class="text-sm font-medium">{{ getAttributeFormControl('attr_' + schema.name)?.value }}</div>
                                        </ng-template>
                                        <ng-template let-option pTemplate="item">
                                          <div class="text-sm">{{ option }}</div>
                                        </ng-template>
                                      </p-select>
                                      @if (getAttributeFormControl('attr_' + schema.name)?.invalid && getAttributeFormControl('attr_' + schema.name)?.touched) {
                                        <small class="p-error text-xs">
                                          Required field
                                        </small>
                                      }
                                    </div>
                                  }
                                  <!-- Boolean Type (Toggle) -->
                                  @if (schema.type.toLowerCase() === 'boolean') {
                                    <div data-testid="attribute-boolean-container">
                                      <div class="flex align-items-center justify-content-between bg-surface-50 border-round p-2">
                                        <label [for]="'attr_' + i" class="text-sm font-medium text-700">
                                          <i class="pi pi-check text-xs mr-1 text-primary"></i>
                                          {{ schema.label || schema.name }}
                                        </label>
                                        <p-toggleSwitch [id]="'attr_' + i"
                                          [formControlName]="'attr_' + schema.name"
                                          data-testid="attribute-toggle" />
                                      </div>
                                    </div>
                                  }
                                  <!-- String Type (Input) -->
                                  @if (schema.type.toLowerCase() === 'string') {
                                    <div data-testid="attribute-string-container">
                                      <label [for]="'attr_' + i" class="block text-sm font-medium text-700 mb-1">
                                        <i class="pi pi-pencil text-xs mr-1 text-primary"></i>
                                        {{ schema.label || schema.name }}
                                        @if (schema.required) {
                                          <sup class="text-red-500">*</sup>
                                        }
                                      </label>
                                      <input
                                        pInputText
                                        [id]="'attr_' + i"
                                        [formControlName]="'attr_' + schema.name"
                                        class="w-full text-sm"
                                        [placeholder]="'Enter ' + (schema.label || schema.name)"
                                        data-testid="attribute-input" />
                                        @if (getAttributeFormControl('attr_' + schema.name)?.invalid && getAttributeFormControl('attr_' + schema.name)?.touched) {
                                          <small class="p-error text-xs">
                                            Required field
                                          </small>
                                        }
                                      </div>
                                    }
                                    <!-- Number Type (Number Input) -->
                                    @if (schema.type.toLowerCase() === 'number') {
                                      <div data-testid="attribute-number-container">
                                        <label [for]="'attr_' + i" class="block text-sm font-medium text-700 mb-1">
                                          <i class="pi pi-hashtag text-xs mr-1 text-primary"></i>
                                          {{ schema.label || schema.name }}
                                          @if (schema.required) {
                                            <sup class="text-red-500">*</sup>
                                          }
                                        </label>
                                        <p-inputNumber [id]="'attr_' + i"
                                          [formControlName]="'attr_' + schema.name"
                                          class="w-full"
                                          [useGrouping]="false"
                                          [showButtons]="true"
                                          buttonLayout="horizontal"
                                          incrementButtonIcon="pi pi-plus"
                                          decrementButtonIcon="pi pi-minus"
                                          placeholder="0"
                                          styleClass="text-sm"
                                          data-testid="attribute-number" />
                                        @if (getAttributeFormControl('attr_' + schema.name)?.invalid && getAttributeFormControl('attr_' + schema.name)?.touched) {
                                          <small class="p-error text-xs">
                                            Required field
                                          </small>
                                        }
                                      </div>
                                    }
                                    <!-- Date Type (Calendar) -->
                                    @if (schema.type.toLowerCase() === 'date') {
                                      <div data-testid="attribute-date-container">
                                        <label [for]="'attr_' + i" class="block text-sm font-medium text-700 mb-1">
                                          <i class="pi pi-calendar text-xs mr-1 text-primary"></i>
                                          {{ schema.label || schema.name }}
                                          @if (schema.required) {
                                            <sup class="text-red-500">*</sup>
                                          }
                                        </label>
                                        <p-datepicker [id]="'attr_' + i"
                                          [formControlName]="'attr_' + schema.name"
                                          class="w-full"
                                          dateFormat="mm/dd/yy"
                                          [showIcon]="true"
                                          iconDisplay="input"
                                          placeholder="Select date"
                                          [showClear]="true"
                                          data-testid="attribute-date" />
                                        @if (getAttributeFormControl('attr_' + schema.name)?.invalid && getAttributeFormControl('attr_' + schema.name)?.touched) {
                                          <small class="p-error text-xs">
                                            Required field
                                          </small>
                                        }
                                      </div>
                                    }
                                  </div>
                                </div>
                              }
                            </div>
                          </div>
                        }
    
                      </form>
                    </div>
    
                    <!-- Footer with Actions -->
                    <div class="border-top p-3 bg-surface-50">
                      <div class="flex justify-content-between align-items-center flex-wrap gap-2">
                        <div class="text-sm text-600">
                          <i class="pi pi-info-circle mr-1"></i>
                          All fields marked with <sup class="text-red-500">*</sup> are required
                        </div>
                        <div class="flex gap-2">
                          <p-button [label]="'RETAIL_BILLING.COMMON.CANCEL' | translate"
                            icon="pi pi-times"
                            [text]="true"
                            severity="secondary"
                            (onClick)="onCancel.emit()"
                            aria-label="cancel-button" />
                          <p-button [label]="'RETAIL_BILLING.COMMON.SAVE' | translate"
                            icon="pi pi-check"
                            [loading]="saving"
                            [disabled]="skuForm.invalid"
                            (onClick)="saveSku()"
                            aria-label="save-sku-button" />
                        </div>
                      </div>
                    </div>
                  </div>
                </p-dialog>
    `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SkuFormComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly skuService = inject(ProductSkuService);
  private readonly supplierService = inject(SupplierService);
  private readonly toastService = inject(ToastMessageService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly commonService = inject(CommonService);

  readonly visible = input(false);
  readonly sku = input<ProductSkuResponse | null>(null);
  readonly product = input<ProductResponse | null>(null);
  readonly tenantId = input<number>();
  readonly companyLevel = input<boolean>(true);
  readonly primaryLanguage = input.required<string>();
  readonly secondaryLanguage = input.required<string>();
  readonly isSecondaryLanguage = input(false);
  
  readonly visibleChange = output<boolean>();
  readonly onSave = output<ProductSkuResponse>();
  readonly onCancel = output<void>();

  skuForm: FormGroup;
  saving = false;
  currentImageUrl: string | null = null;
  /** S3 URL from GET — used to detect unchanged vs removed image on save */
  private originalImageUrl: string | null = null;
  productAttributeSchema: any[] = [];
  supplierOptions: { name: string; value: number }[] = [];
  private destroy$ = new Subject<void>();

  get isEditMode(): boolean {
    return !!this.sku();
  }

  get attributesArray(): FormArray {
    return this.skuForm.get('attributes') as FormArray;
  }

  getAttributeFormControl(controlName: string) {
    return this.skuForm.get(controlName);
  }

  trackBySchema(index: number, schema: any): any {
    return schema.name;
  }

  constructor() {
    this.skuForm = this.createForm();

    effect(() => {
      const visible = this.visible();
      const sku = this.sku();

      if (!visible) {
        return;
      }

      untracked(() => {
        this.loadSuppliers();
        this.initializeProductAttributeSchema();
        if (sku) {
          this.populateForm();
        } else {
          this.resetForm();
        }
        this.updateFormValidation();
      });
    });
  }

  ngOnInit() {
    this.updateFormValidation();
  }

  loadSuppliers() {
    this.supplierService.getCompanySuppliers({
      page: 1,
      page_size: RETAIL_LOOKUP_PAGE_SIZE,
      is_active: true
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.supplierOptions = (response.items || []).map((supplier: SupplierResponse) => ({
            name: supplier.name_primary_language || '',
            value: supplier.id
          }));
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          this.toastService.showError(error);
        }
      });
  }

  initializeProductAttributeSchema() {
    const product = this.product();
    if (product?.attribute_schema) {
      this.productAttributeSchema = product.attribute_schema;
      // Map numeric types to string types for the template
      // this.productAttributeSchema = this.product.attribute_schema.map(attr => ({
      //   ...attr,
      //   type: this.mapTypeFromNumber(attr.type)
      // }));
      
      // Recreate the form to include attribute fields
      this.createFormWithAttributes();
    } else {
      this.productAttributeSchema = [];
    }
  }

  mapTypeFromNumber(typeNum: number): string {
    const typeMap: Record<number, string> = {
      0: 'Enum',
      1: 'Boolean', 
      2: 'String',
      3: 'Number',
      4: 'Date'
    };
    return typeMap[typeNum] || 'string';
  }

  updateFormValidation() {
    if (this.skuForm) {
      const nameSecondaryControl = this.skuForm.get('variant_name_secondary_language');
      if (this.isSecondaryLanguage()) {
        nameSecondaryControl?.setValidators([Validators.required]);
      } else {
        nameSecondaryControl?.clearValidators();
      }
      nameSecondaryControl?.updateValueAndValidity();

      // Update attribute validations
      this.productAttributeSchema.forEach(schema => {
        const attributeControl = this.skuForm.get('attr_' + schema.name);
        if (attributeControl) {
          if (schema.required) {
            attributeControl.setValidators([Validators.required]);
          } else {
            attributeControl.clearValidators();
          }
          attributeControl.updateValueAndValidity();
        }
      });
    }
  }

  createForm(): FormGroup {
    return this.fb.group({
      sku_code: [''],
      variant_name_primary_language: ['', Validators.required],
      variant_name_secondary_language: [''],
      unit_price_minor: [0, [Validators.required, Validators.min(0)]],
      cost_price_minor: [0, [Validators.required, Validators.min(0)]],
      online_price_minor: [null],
      description_primary_language: [''],
      description_secondary_language: [''],
      shipping_weight: [null],
      supplier_id: [null],
      supplier_sku_id: [''],
      barcode: [''],
      inventory_uom: [''],
      reorder_level: [0],
      max_inventory: [0],
      track_inventory: [true],
      is_active: [true],
      is_default: [false],
      image_url: [''],
      attributes: this.fb.array([])
    });
  }

  createFormWithAttributes(): void {
    const baseFormConfig: any = {
      sku_code: [this.skuForm?.get('sku_code')?.value || ''],
      variant_name_primary_language: [this.skuForm?.get('variant_name_primary_language')?.value || '', Validators.required],
      variant_name_secondary_language: [this.skuForm?.get('variant_name_secondary_language')?.value || ''],
      unit_price_minor: [this.skuForm?.get('unit_price_minor')?.value || 0, [Validators.required, Validators.min(0)]],
      cost_price_minor: [this.skuForm?.get('cost_price_minor')?.value || 0, [Validators.required, Validators.min(0)]],
      online_price_minor: [this.skuForm?.get('online_price_minor')?.value ?? null],
      description_primary_language: [this.skuForm?.get('description_primary_language')?.value || ''],
      description_secondary_language: [this.skuForm?.get('description_secondary_language')?.value || ''],
      shipping_weight: [this.skuForm?.get('shipping_weight')?.value ?? null],
      supplier_id: [this.skuForm?.get('supplier_id')?.value ?? null],
      supplier_sku_id: [this.skuForm?.get('supplier_sku_id')?.value || ''],
      barcode: [this.skuForm?.get('barcode')?.value || ''],
      inventory_uom: [this.skuForm?.get('inventory_uom')?.value || ''],
      reorder_level: [this.skuForm?.get('reorder_level')?.value || 0],
      max_inventory: [this.skuForm?.get('max_inventory')?.value || 0],
      track_inventory: [this.skuForm?.get('track_inventory')?.value !== undefined ? this.skuForm?.get('track_inventory')?.value : true],
      is_active: [this.skuForm?.get('is_active')?.value !== undefined ? this.skuForm?.get('is_active')?.value : true],
      is_default: [this.skuForm?.get('is_default')?.value !== undefined ? this.skuForm?.get('is_default')?.value : false],
      image_url: [this.skuForm?.get('image_url')?.value || ''],
      attributes: this.fb.array([])
    };

    // Add attribute form controls
    this.productAttributeSchema.forEach(schema => {
      const validators = schema.required ? [Validators.required] : [];
      let defaultValue: any = null;
      
      // Set default values based on type
      switch (schema.type.toLowerCase()) {
        case 'boolean':
          defaultValue = false;
          break;
        case 'number':
          defaultValue = 0;
          break;
        case 'string':
        case 'enum':
        case 'date':
        default:
          defaultValue = null;
          break;
      }

      // Preserve existing values if form already exists
      const existingValue = this.skuForm?.get('attr_' + schema.name)?.value;
      baseFormConfig['attr_' + schema.name] = [existingValue !== undefined ? existingValue : defaultValue, validators];
    });

    this.skuForm = this.fb.group(baseFormConfig);
  }

  populateForm() {
    const sku = this.sku();
    if (sku) {
      const patchData: any = {
        sku_code: sku.sku_code,
        variant_name_primary_language: sku.variant_name_primary_language || '',
        variant_name_secondary_language: sku.variant_name_secondary_language || '',
        unit_price_minor: this.commonService.ConvertAmountFromMinorCurrency(sku.unit_price_minor),
        cost_price_minor: this.commonService.ConvertAmountFromMinorCurrency(sku.cost_price_minor),
        online_price_minor: sku.online_price_minor != null
          ? this.commonService.ConvertAmountFromMinorCurrency(sku.online_price_minor)
          : null,
        description_primary_language: sku.description_primary_language || '',
        description_secondary_language: sku.description_secondary_language || '',
        shipping_weight: sku.shipping_weight ?? null,
        supplier_id: sku.supplier_id ?? null,
        supplier_sku_id: sku.supplier_sku_id || '',
        barcode: sku.barcode,
        inventory_uom: sku.inventory_uom,
        reorder_level: sku.reorder_level,
        max_inventory: sku.max_inventory,
        track_inventory: sku.track_inventory,
        is_active: sku.is_active,
        is_default: sku.is_default,
        image_url: sku.image_url
      };

      // Populate product attributes from SKU attributes
      if (sku.attributes_json) {
        sku.attributes_json.forEach((attrObj: any) => {
          // Each attribute is an object like {"Size": "S", "Color": "Red"}
          Object.keys(attrObj).forEach(attrName => {
            const attrValue = attrObj[attrName];
            // Map SKU attributes to form controls
            patchData['attr_' + attrName] = attrValue;
          });
        });
      }

      this.skuForm.patchValue(patchData);
      this.originalImageUrl = sku.image_url || null;
      this.currentImageUrl = sku.image_url || null;
    }
  }

  resetForm() {
    if (this.productAttributeSchema.length > 0) {
      this.createFormWithAttributes();
    } else {
      this.skuForm = this.createForm();
    }
    this.originalImageUrl = null;
    this.currentImageUrl = null;
  }

  createAttributeFormGroup(attr?: ProductSkuAttribute): FormGroup {
    return this.fb.group({
      name: [attr?.name || '', Validators.required],
      value: [attr?.value || '', Validators.required],
      price_adjustment: [attr?.price_adjustment || 0]
    });
  }

  addAttribute() {
    this.attributesArray.push(this.createAttributeFormGroup());
  }

  removeAttribute(index: number) {
    this.attributesArray.removeAt(index);
  }

  handleFileSelect(files: NgxFileDropEntry[]) {
    if (files && files.length > 0) {
      const file = files[0];
      if (file.fileEntry.isFile) {
        const fileEntry = file.fileEntry as FileSystemFileEntry;
        fileEntry.file((uploadedFile: File) => {
          this.processFile(uploadedFile);
        });
      }
    }
  }

  processFile(file: File) {
    // Create a preview URL for the image
    const reader = new FileReader();
    reader.onload = (e) => {
      this.currentImageUrl = e.target?.result as string;
      this.skuForm.patchValue({ image_url: this.currentImageUrl });
      this.changeDetectorRef.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  removeImage() {
    this.currentImageUrl = null;
    this.skuForm.patchValue({ image_url: '' });
  }

  saveSku() {
    const product = this.product();
    if (this.skuForm.valid && product) {
      this.saving = true;
      const formData = this.skuForm.value;

      // Build attributes array from product attribute schema
      const attributes: any[] = [];
      this.productAttributeSchema.forEach(schema => {
        const attributeValue = formData['attr_' + schema.name];
        const priceAdjustment = formData['price_adj_' + schema.name] || 0;
        
        if (attributeValue !== null && attributeValue !== undefined && attributeValue !== '') {
          attributes.push({
            [schema.name] : attributeValue
          });
        }
      });

      const imagePayload = buildRetailImageWritePayload({
        currentImage: this.currentImageUrl ?? formData.image_url,
        originalImageUrl: this.originalImageUrl,
        isEdit: this.isEditMode,
      });

      const request: CreateProductSkuRequest | UpdateProductSkuRequest = {
        sku_code: formData.sku_code,
        variant_name_primary_language: formData.variant_name_primary_language || '',
        variant_name_secondary_language: formData.variant_name_secondary_language || '',
        unit_price_minor: this.commonService.ConvertMinorCurrencyFromAmount(formData.unit_price_minor),
        cost_price_minor: this.commonService.ConvertMinorCurrencyFromAmount(formData.cost_price_minor),
        online_price_minor: formData.online_price_minor != null && formData.online_price_minor !== ''
          ? this.commonService.ConvertMinorCurrencyFromAmount(formData.online_price_minor)
          : null,
        description_primary_language: formData.description_primary_language || null,
        description_secondary_language: formData.description_secondary_language || null,
        shipping_weight: formData.shipping_weight ?? null,
        supplier_id: formData.supplier_id ?? null,
        supplier_sku_id: formData.supplier_sku_id || null,
        barcode: formData.barcode,
        inventory_uom: formData.inventory_uom,
        reorder_level: formData.reorder_level,
        max_inventory: formData.max_inventory,
        track_inventory: formData.track_inventory,
        is_active: formData.is_active,
        is_default: formData.is_default,
        attributes_json: attributes,
        ...imagePayload,
      };

      // For create operations, we need to wrap in array for bulk create
      const saveOperation = this.isEditMode
        ? this.companyLevel()
          ? this.skuService.updateCompanyProductSku(product.id, this.sku()!.id, request as UpdateProductSkuRequest)
          : this.skuService.updateTenantProductSku(this.tenantId()!, product.id, this.sku()!.id, request as UpdateProductSkuRequest)
        : this.companyLevel()
          ? this.skuService.createCompanyProductSkus(product.id, { skus: [request as CreateProductSkuRequest] })
          : this.skuService.createTenantProductSkus(this.tenantId()!, product.id, { skus: [request as CreateProductSkuRequest] });

      (saveOperation as any)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.saving = false;
            // Create a mock SKU response for the event
            const savedSku: ProductSkuResponse = {
              ...this.sku(),
              id: this.sku()?.id || 0,
              product_id: this.product()!.id,
              company_id: this.sku()?.company_id || 1,
              tenant_id: this.sku()?.tenant_id || this.tenantId() || 0,
              ...request,
              created_at: this.sku()?.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: this.sku()?.created_by || 1,
              updated_by: 1
            } as ProductSkuResponse;
            
            this.onSave.emit(savedSku);
            this.visibleChange.emit(false);
          },
          error: (error:any) => {
            this.saving = false;
            this.toastService.showError(error);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.visibleChange.emit(false);
  }
}